import type { ItemDef, PlacedSnapshot } from "./types";
import { MONSTERS } from "./monsters";
import { ITEMS } from "./items";
import { state } from "./state";
import { $, monEl, monStage, trayEl } from "../dom";
import { rebuildBoard, spaceUsed } from "./board";
import { piecesMap, hoardValue, sizePiece } from "./pieces";
import { rankFor } from "./scoring";
import { openComparison, prepareSolves, resetReveal } from "../reveal/reveal";

/** Sum of all banked stages plus the current (unbanked) stage. */
function runTotal(): number {
  return state.stages.reduce((s, r) => s + r.value, 0) + hoardValue();
}

/* ---------- HUD ---------- */
export function updateHUD(): void {
  $("encVal").textContent = Math.min(state.monsterIndex + 1, MONSTERS.length) + " / " + MONSTERS.length;
  $("stageVal").textContent = String(hoardValue());
  $("totalVal").textContent = String(runTotal());
  $("spaceVal").textContent = spaceUsed() + " / " + state.cols * state.rows;
}

export function renderHp(): void {
  const mon = MONSTERS[state.monsterIndex];
  const hp = mon._hp ?? mon.hp;
  const pct = Math.max(0, (hp / mon.hp) * 100);
  $("hpFill").style.width = pct + "%";
  $("hpText").textContent = hp + " / " + mon.hp;
}

export function refreshTrayState(): void {
  const hasTray = [...piecesMap.values()].some((p) => p.location === "tray");
  trayEl.classList.toggle("empty-state", !hasTray);
}

/* ---------- Encounter flow ---------- */
/** Set the current stage's inventory size and rebuild the (empty) board. */
function setStageGrid(): void {
  const g = MONSTERS[state.monsterIndex].grid;
  state.cols = g.cols;
  state.rows = g.rows;
  rebuildBoard();
}

export function startEncounter(): void {
  // each stage is a fresh puzzle — clear any leftover pieces and resize the grid
  for (const p of piecesMap.values()) p.el.remove();
  piecesMap.clear();
  setStageGrid();

  state.phase = "fight";
  const mon = MONSTERS[state.monsterIndex];
  mon._hp = mon.hp;
  $("monName").textContent = mon.name;
  $("monFlavor").textContent = mon.flavor;
  monEl.textContent = mon.icon;
  monEl.className = "mon alive";
  monStage.classList.remove("looted");
  $("atkHint").textContent = "— strike the beast to make it drop its hoard —";
  $("proceedRow").innerHTML = "";
  refreshTrayState();
  renderHp();
  layout();
  updateHUD();
}

export function renderProceed(): void {
  const last = state.monsterIndex === MONSTERS.length - 1;
  const row = $("proceedRow");
  row.innerHTML =
    `<div class="warn">Anything left in the pile — and everything you can't fit — is lost.</div>
     <button class="btn" id="proceedBtn">${last ? "Escape the Dungeon" : "Press On →"}</button>`;
  $("proceedBtn").addEventListener("click", proceed);
}

/** Freeze how the player packed the current stage, for the end comparison. */
function recordStage(): void {
  const pieces: PlacedSnapshot[] = [];
  let value = 0;
  for (const p of piecesMap.values()) {
    if (p.location === "grid" && p.pos) {
      pieces.push({ key: p.key, r: p.pos.r, c: p.pos.c, cells: p.cells.map((c) => [...c] as [number, number]), value: p.item.value });
      value += p.item.value;
    }
  }
  state.stages.push({ monsterIndex: state.monsterIndex, cols: state.cols, rows: state.rows, value, pieces });
}

function proceed(): void {
  recordStage();
  state.monsterIndex++;
  if (state.monsterIndex >= MONSTERS.length) endGame();
  else startEncounter();
}

/* ---------- End ---------- */
function endGame(): void {
  state.phase = "done";
  const total = state.stages.reduce((s, r) => s + r.value, 0);
  $("endVal").textContent = String(total);
  $("endRank").textContent = rankFor(total);

  // breakdown of everything carried out across all five stages
  const counts: Record<string, { n: number; item: ItemDef }> = {};
  for (const st of state.stages) {
    for (const pc of st.pieces) {
      if (!counts[pc.key]) counts[pc.key] = { n: 0, item: ITEMS[pc.key] };
      counts[pc.key].n++;
    }
  }
  const haul = $("endHaul");
  haul.innerHTML = "";
  const entries = Object.values(counts).sort((a, b) => b.item.value - a.item.value);
  if (entries.length === 0) {
    haul.innerHTML = `<div class="hitem">You carried out… nothing.</div>`;
  } else {
    for (const e of entries) {
      const d = document.createElement("div");
      d.className = "hitem";
      d.innerHTML = `${e.item.icon} ${e.item.name}${e.n > 1 ? " ×" + e.n : ""} · <b>${e.item.value * e.n}</b>`;
      haul.appendChild(d);
    }
  }
  setupEndActions(total);
  $("endOverlay").classList.remove("hidden");
}

/** End-overlay actions. Kicks off all five stage solves and refines the copy
 *  once the optimal run total is known. */
function setupEndActions(total: number): void {
  const wrap = $("endActions");
  wrap.innerHTML =
    `<div class="opt-prompt" id="optPrompt">Think you packed well? See how a solver would have done it — stage by stage.</div>
     <button class="btn" id="revealBtn">Reveal: You vs the Optimizer ▸</button>`;
  $("revealBtn").addEventListener("click", openComparison);

  prepareSolves()
    .then((results) => {
      const optTotal = results.reduce((s, r) => s + (r ? r.value : 0), 0);
      const prompt = $("optPrompt");
      if (!prompt) return;
      prompt.innerHTML = total >= optTotal
        ? `✦ A flawless run — you matched the proven optimum of <b>${optTotal}</b> across all five stages.`
        : `A perfect run could have carried <b>${optTotal}</b> across the five stages. See how, stage by stage.`;
    })
    .catch(() => { /* solver unreachable — keep the neutral prompt */ });
}

export function restart(): void {
  for (const p of piecesMap.values()) p.el.remove();
  piecesMap.clear();
  state.nextId = 1;
  state.monsterIndex = 0;
  state.stages = [];
  $("endOverlay").classList.add("hidden");
  $("solverOverlay").classList.add("hidden");
  $("learnOverlay").classList.add("hidden");
  resetReveal();
  startEncounter();
}

/* ---------- Responsive layout ---------- */
export function layout(): void {
  const holder = document.querySelector(".grid-holder") as HTMLElement;
  const avail = holder.clientWidth || 460;
  state.cellPx = Math.max(34, Math.min(64, Math.floor((avail - 12) / state.cols)));
  document.documentElement.style.setProperty("--cell", state.cellPx + "px");
  for (const p of piecesMap.values()) sizePiece(p);
}
